import { createMustacheFunc, MustacheFunc } from '.';
import { getPreloadedFont } from './fontLoader';

type GridSizingParams = {
  dataSource: string;
  header: string[];
  row: string[];
  footer?: string[];
  tableWidth: number; // wrapper width
  gridId?: string; // unique identifier for this grid
  /**
   * Renderer-specific height adjustment multiplier for data rows.
   * Use this to compensate for non-standard line height rendering (e.g., ReactPDF).
   * Default: 1.5
   */
  rowHeightScalar?: number;
  /**
   * Renderer-specific height adjustment multiplier for header/footer rows.
   * Use this to compensate for non-standard line height rendering (e.g., ReactPDF).
   * Default: 1.5
   */
  headerFooterHeightScalar?: number;
  /** Safety padding in pixels added to text width measurements to prevent wrapping. Default: 1. */
  safetyPadX?: number;
  /** Optional per-column styles (applied per cell kind). Arrays must be length = COLS; missing fields fall back to defaults. */
  cellStyles?: { header?: Partial<CellStyle>[]; row?: Partial<CellStyle>[]; footer?: Partial<CellStyle>[] };
};

export type CellStyle = {
  padX: number; // px, each side
  padY: number; // px, each side
  borderX: number; // px, each side
  borderY: number; // px, each side
  fontWeight: number; // 100..900
  fontSize: number; // px (dynamic)
  lineHeightPx: number; // px, effective renderer line height
};

// defaults (fallbacks only)
const CLASS_PREFIX = 'grid';
const MIN_COL_PX = 50;

const DEF_PAD_X = 8;
const DEF_PAD_Y = 2;
const DEF_BORDER_X = 0;
const DEF_BORDER_Y = 1;

const DEF_FONT_SIZE = 9; // fallback if not specified
const WEIGHT_BIAS = 1.08; // ~8% wider when bold
const SAFETY_PAD_PX = 1;

const RS = '\u241E';

// keep your existing CSS height adjustments
const _DEF_LINE_PX = 11; // used only in CSS fudge below, per your request

function renderColumnSeries(dsPath: string, colTpl: string, render: (s: string) => string): string[] {
  const expr = /\{\{/.test(colTpl) ? colTpl : `{{{${colTpl}}}}`;
  const wrapped = `{{#${dsPath}}}${expr}${RS}{{/${dsPath}}}`;
  const out = render(wrapped) ?? '';
  const parts = out.split(RS);
  if (parts.length && parts[parts.length - 1] === '') parts.pop();
  return parts;
}

function renderBodyRows(dsPath: string, rowTpls: string[], render: (s: string) => string): string[][] {
  if (!rowTpls.length) return [];
  const cols: string[][] = rowTpls.map((tpl) => renderColumnSeries(dsPath, tpl, render));
  const rowCount = Math.max(...cols.map((c) => c.length));
  const colCount = rowTpls.length;
  return Array.from({ length: rowCount }, (_, r) => Array.from({ length: colCount }, (_, c) => cols[c][r] ?? ''));
}

const measureTable: () => MustacheFunc = <T extends GridSizingParams>() =>
  createMustacheFunc(function (_content, render, params: T) {
    if (!params?.tableWidth || params.tableWidth <= 0) return '';

    const safetyPadX = params.safetyPadX ?? SAFETY_PAD_PX;

    // --- font / measure ---
    const getFontForWeight = (fontWeight: number): any => {
      // Try to get preloaded font first
      const preloadedFont = getPreloadedFont(fontWeight, 'normal');
      if (preloadedFont) {
        return preloadedFont;
      }

      // Fallback to weight 400 if specific weight not preloaded
      const fallbackFont = getPreloadedFont(400, 'normal');
      if (fallbackFont) {
        return fallbackFont;
      }

      throw new Error('Font weight 400 not preloaded - ensure preloadCommonFonts() is called');
    };

    const measureBase = (text: string, fontSize: number, fontWeight: number): number => {
      if (!text) return 0;

      const fkFont = getFontForWeight(fontWeight);
      const unitsPerEm = fkFont.unitsPerEm || 1000;
      const features = { liga: true, kern: true };

      const shaped = fkFont.layout ? fkFont.layout(text, features as any) : fkFont.shape(text, features as any);
      const positions = shaped.positions || [];
      let adv = 0;
      for (let i = 0; i < positions.length; i++) adv += positions[i].xAdvance || 0;
      return (adv / unitsPerEm) * fontSize;
    };

    const measureWithStyle = (text: string, st: CellStyle): number => {
      return measureBase(text, st.fontSize, st.fontWeight) * WEIGHT_BIAS + safetyPadX;
    };

    // --- style helpers ---
    const resolveStyle = (s: Partial<CellStyle> | undefined, defaults: { bold?: boolean }): CellStyle => {
      const fontSize = s?.fontSize ?? DEF_FONT_SIZE;
      const fontWeight = s?.fontWeight ?? (defaults.bold ? 700 : 400);

      // Calculate actual line height from font metrics if not explicitly provided
      let lineHeightPx: number;
      try {
        const font = getFontForWeight(fontWeight);
        // Use actual font metrics: (ascent + |descent| + lineGap) / unitsPerEm * fontSize
        const ascent = font.ascent || 0;
        const descent = Math.abs(font.descent || 0);
        const lineGap = font.lineGap || 0;
        const unitsPerEm = font.unitsPerEm || 1000;
        lineHeightPx = Math.round(((ascent + descent + lineGap) / unitsPerEm) * fontSize);
      } catch {
        // Fallback if font metrics unavailable
        lineHeightPx = Math.round(fontSize * 1.25);
      }

      return {
        padX: s?.padX ?? DEF_PAD_X,
        padY: s?.padY ?? DEF_PAD_Y,
        borderX: s?.borderX ?? DEF_BORDER_X,
        borderY: s?.borderY ?? DEF_BORDER_Y,
        fontWeight,
        fontSize,
        lineHeightPx,
      };
    };

    const headerCells = (params.header ?? []).map((t) => String(render(t) ?? ''));
    const footerCells = (params.footer ?? []).map((t) => String(render(t) ?? ''));
    const bodyRows = renderBodyRows(params.dataSource, params.row ?? [], render);

    const COLS = (params.row?.length || params.header?.length || params.footer?.length || 0) | 0;
    if (!COLS) return '';

    const headerStyleArr = Array.from({ length: COLS }, (_, c) =>
      resolveStyle(params.cellStyles?.header?.[c], { bold: true })
    );
    const rowStyleArr = Array.from({ length: COLS }, (_, c) =>
      resolveStyle(params.cellStyles?.row?.[c], { bold: false })
    );
    const footerStyleArr = Array.from(
      { length: COLS },
      (_, c) => resolveStyle(params.cellStyles?.footer?.[c], { bold: true }) // footers default bold-ish; change if you like
    );

    // --- collect per-column extremes in content-space, and max padding/border per column ---
    type ColAccum = {
      minToken: number; // widest token across header/body/footer with their own styles
      maxFull: number; // widest full string across header/body/footer
      headerFull: number; // header full width (used for one-line guard)
      padX: number; // max across header/body/footer (wrapper calc)
      borderX: number; // max across header/body/footer (wrapper calc)
    };
    const cols: ColAccum[] = Array.from({ length: COLS }, () => ({
      minToken: MIN_COL_PX,
      maxFull: MIN_COL_PX,
      headerFull: 0,
      padX: DEF_PAD_X,
      borderX: DEF_BORDER_X,
    }));

    // header
    headerCells.forEach((text, c) => {
      const st = headerStyleArr[c];
      const full = measureWithStyle(text, st);
      cols[c].maxFull = Math.max(cols[c].maxFull, full, MIN_COL_PX);
      cols[c].headerFull = Math.max(cols[c].headerFull, full);
      text
        .split(/\s+/)
        .filter(Boolean)
        .forEach((w) => {
          cols[c].minToken = Math.max(cols[c].minToken, measureWithStyle(w, st));
        });
      cols[c].padX = Math.max(cols[c].padX, st.padX);
      cols[c].borderX = Math.max(cols[c].borderX, st.borderX);
    });

    // body
    for (const r of bodyRows) {
      r.forEach((text, c) => {
        const st = rowStyleArr[c];
        const full = measureWithStyle(text || '', st);
        cols[c].maxFull = Math.max(cols[c].maxFull, full, MIN_COL_PX);
        (text || '')
          .split(/\s+/)
          .filter(Boolean)
          .forEach((w) => {
            cols[c].minToken = Math.max(cols[c].minToken, measureWithStyle(w, st));
          });
        cols[c].padX = Math.max(cols[c].padX, st.padX);
        cols[c].borderX = Math.max(cols[c].borderX, st.borderX);
      });
    }

    // footer
    footerCells.forEach((text, c) => {
      const st = footerStyleArr[c];
      const full = measureWithStyle(text, st);
      cols[c].maxFull = Math.max(cols[c].maxFull, full, MIN_COL_PX);
      text
        .split(/\s+/)
        .filter(Boolean)
        .forEach((w) => {
          cols[c].minToken = Math.max(cols[c].minToken, measureWithStyle(w, st));
        });
      cols[c].padX = Math.max(cols[c].padX, st.padX);
      cols[c].borderX = Math.max(cols[c].borderX, st.borderX);
    });

    // --- envelopes (wrapper space) ---
    type Envelope = {
      minW: number; // wrapper min = minToken + 2*padX + 2*borderX
      maxW: number; // wrapper max = max(maxFull, headerFull) + padding/border
      needHeaderW: number; // wrapper width to keep header one-line
      needContentOneLine: number; // content-space requirement for rounding guard
      padX: number;
      borderX: number;
    };

    const envs: Envelope[] = cols.map((c) => {
      const minContent = Math.max(MIN_COL_PX, c.minToken);
      const bodyOrHeader = Math.max(c.maxFull, c.headerFull); // content
      const minW = minContent + 2 * c.padX + 2 * c.borderX;
      const maxW = bodyOrHeader + 2 * c.padX + 2 * c.borderX;
      const needHeaderW = c.headerFull + 2 * c.padX + 2 * c.borderX;
      return { minW, maxW, needHeaderW, needContentOneLine: bodyOrHeader, padX: c.padX, borderX: c.borderX };
    });

    // --- width solve: mins → header one-line guards → fair fill to max ---
    const sum = (a: number[]) => a.reduce((x, y) => x + y, 0);
    const tableW = params.tableWidth;

    const widthsF = envs.map((e) => e.minW);
    let leftover = Math.max(0, tableW - sum(widthsF));

    // Phase 1: raise to header one-line targets where possible
    if (leftover > 0) {
      type Need = { i: number; deficit: number };
      let needs: Need[] = envs
        .map((e, i) => ({ i, deficit: Math.max(0, Math.min(e.maxW, Math.max(e.minW, e.needHeaderW)) - widthsF[i]) }))
        .filter((n) => n.deficit > 0);

      while (leftover > 0 && needs.length) {
        const addEach = Math.max(1, Math.floor(leftover / needs.length));
        let added = 0;
        for (const n of needs) {
          const inc = Math.min(addEach, n.deficit);
          widthsF[n.i] += inc;
          n.deficit -= inc;
          added += inc;
        }
        if (added === 0) break;
        leftover -= added;
        needs = needs.filter((n) => n.deficit > 0);
      }
    }

    // Phase 2: fair fill toward max
    if (leftover > 0) {
      const slack = envs.map((e, i) => Math.max(0, e.maxW - widthsF[i]));
      while (leftover > 0) {
        const active = slack.map((s, i) => (s > 0 ? i : -1)).filter((i) => i >= 0);
        if (!active.length) break;
        const addEach = Math.max(1, Math.floor(leftover / active.length));
        let added = 0;
        for (const i of active) {
          const inc = Math.min(addEach, slack[i]);
          widthsF[i] += inc;
          slack[i] -= inc;
          added += inc;
        }
        if (added === 0) break;
        leftover -= added;
      }
    }

    // Keep floats for wrapping; compute ints for CSS with pressure-aware rounding
    const colWidthF = widthsF.slice();

    const contentLimitF = (w: number, padX: number, borderX: number) => Math.max(1, w - 2 * padX - 2 * borderX);

    type Pressure = { i: number; deficit: number; frac: number };
    const pressures: Pressure[] = envs.map((e, i) => {
      const limit = contentLimitF(colWidthF[i], e.padX, e.borderX);
      return { i, deficit: Math.max(0, e.needContentOneLine - limit), frac: colWidthF[i] - Math.floor(colWidthF[i]) };
    });

    let colInt = colWidthF.map(Math.floor);
    let used = sum(colInt);
    let rem = Math.max(0, tableW - used);

    pressures.sort((a, b) => b.deficit - a.deficit || b.frac - a.frac);
    for (let k = 0; k < pressures.length && rem > 0; k++) {
      colInt[pressures[k].i] += 1;
      rem--;
    }
    if (rem > 0) {
      const byFrac = pressures
        .slice()
        .sort((a, b) => b.frac - a.frac)
        .map((p) => p.i);
      let idx = 0;
      while (rem-- > 0) colInt[byFrac[idx++ % byFrac.length]] += 1;
    }

    const colWidthInts = colInt;

    // --- wrap & heights (per-row, per-cell style & fontSize) ---
    const wrapLines = (text: string, limit: number, st: CellStyle): number => {
      if (!text) return 1;
      const words = text.split(/\s+/).filter(Boolean);
      if (!words.length) return 1;

      let line = '';
      let lines = 1;

      for (const token of words) {
        const cand = line ? line + ' ' + token : token;
        if (measureWithStyle(cand, st) <= limit) {
          line = cand;
        } else if (!line && measureWithStyle(token, st) > limit) {
          // break long token
          let rest = token;
          while (rest.length && measureWithStyle(rest, st) > limit) {
            let lo = 1,
              hi = rest.length,
              best = 1;
            while (lo <= hi) {
              const mid = (lo + hi) >> 1;
              if (measureWithStyle(rest.slice(0, mid), st) <= limit) {
                best = mid;
                lo = mid + 1;
              } else hi = mid - 1;
            }
            lines++;
            rest = rest.slice(best);
          }
          line = rest;
          if (!rest) lines = Math.max(1, lines - 1);
        } else {
          lines++;
          line = token;
        }
      }
      return lines;
    };

    const measureRowLike = (cells: string[], kind: 'header' | 'row' | 'footer'): number => {
      let maxPx = 0;
      for (let c = 0; c < COLS; c++) {
        const st = kind === 'header' ? headerStyleArr[c] : kind === 'footer' ? footerStyleArr[c] : rowStyleArr[c];
        const limit = Math.max(1, colWidthF[c] - 2 * st.padX - 2 * st.borderX);
        const hLines = wrapLines(cells[c] ?? '', limit, st);
        const cellPx = hLines * st.lineHeightPx + 2 * st.padY + 2 * st.borderY;
        if (cellPx > maxPx) maxPx = cellPx;
      }
      return maxPx;
    };

    const headerHeight = headerCells.length ? measureRowLike(headerCells, 'header') : 0;
    const rowHeights = bodyRows.map((r) => measureRowLike(r, 'row'));
    const footerHeight = footerCells.length ? measureRowLike(footerCells, 'footer') : 0;

    // --- emit widths/heights only (keep your CSS adjustments) ---
    const gridPrefix = params.gridId ? `${CLASS_PREFIX}-${params.gridId}` : CLASS_PREFIX;
    const rowHeightScalar = params.rowHeightScalar ?? 1.5;
    const headerFooterHeightScalar = params.headerFooterHeightScalar ?? 1.5;

    const cssCols = colWidthInts.map((w, i) => `.${gridPrefix}-col-${i}{width:${w}px;}`).join('');
    const cssRows = rowHeights.map((h, i) => `.${gridPrefix}-row-${i}{height:${h * rowHeightScalar}px;}`).join('');
    const cssHeader = headerHeight ? `.${gridPrefix}-header{height:${headerHeight * headerFooterHeightScalar}px;}` : '';
    const cssFooter = footerHeight ? `.${gridPrefix}-footer{height:${footerHeight * headerFooterHeightScalar}px;}` : '';

    const css = `${cssCols}${cssRows}${cssHeader}${cssFooter}`;

    return `<style>${css}</style><!-- ${JSON.stringify({
      columns: colWidthInts,
      rows: rowHeights,
      header: headerHeight || undefined,
      footer: footerHeight || undefined,
      classPrefix: gridPrefix,
    })} -->`;
  });

export default measureTable;
